import React, { useMemo, useState, useEffect, useRef } from 'react';
import { formatDate } from '../../utilities/dateHelpers';
import {
  useTable,
  useSortBy,
  Column,
  useFilters,
  useGlobalFilter,
  TableInstance,
  TableState,
  UseGlobalFiltersInstanceProps,
  UseGlobalFiltersState,
} from 'react-table';
import TableLegend from './TableLegend';
import { TableDataItem } from '../../types';
import { fetchWellTestData } from './../../dispatchers/WellTestDispatcher';
import { useAppSelector, useAppDispatch } from '../../hooks/storeHooks';
import './DataTable.scss';
import AddWellTestModal from '../../features/add-well-test/addWellTestModal';
import { mapTableDataToFormData } from '../../utilities/WellTestMappers';
import { mapGLTableDataToFormData } from '../../utilities/mapGLTableDataToFormData';
import Loader from '../../features/common/Loader/Loader';
import { ReactComponent as ArrowUp } from '../../images/arrow-narrow-up.svg';
import { ReactComponent as ArrowDown } from '../../images/arrow-narrow-down.svg';
import { ReactComponent as InfoIcon } from '../../images/info-circle-1.svg';
import { ReactComponent as CommentIcon } from '../../images/message-square-02.svg';
import Tooltip from '../../features/common/tooltip/ToolTip';
import ReactDOMServer from 'react-dom/server';
import CommentPopup from '../info-popup/CommentPopup';
import InfoPopup from '../info-popup/InfoPopup';
import { DefaultColumnFilter } from './helpers/DefaultColumnFilter';
import AddGLWellTestModal from '../../features/add-glwell-test/addGLWelltestModal';
import AddDefaultWellTestModal from '../../features/add-defaultwell-test/addDefaultWellTestModal';
import { mapDefaultWellTestTableDataToForm } from './helpers/DataTableMapTableDataToForm';
import useConfig from '../../hooks/useConfig';
import { createMultiConditionFilter } from '../../utilities/tableFilters';
import { isNumericField as originalIsNumericField } from '../../features/recommendations/Table-widgets/TableData/RecommendationsTableData';
import { ProtectAction } from '../../rbac';

interface DataTableProps {
  ref: any;
  visibleColumns: string[];
  onUpdateVisibleColumns: (columns: string[]) => void;
  searchTerm?: string;
  applyTrigger: boolean;
  isResetClicked?: boolean;
  installNumber: number | null;
  onFilteredDataChange?: (filteredData: any[]) => void;
  rPermission: { [key: string]: boolean };
}

interface ColumnData {
  order: number;
  label: string;
  key: string;
  selected: boolean;
}

// Extend TableInstance and TableState types to include global filter properties
interface ExtendedTableInstance<T extends object> extends TableInstance<T>, UseGlobalFiltersInstanceProps<T> {}
interface ExtendedTableState<T extends object> extends TableState<T>, UseGlobalFiltersState<T> {}

// Define a default UI for filtering
const DataTable: React.FC<DataTableProps> = React.forwardRef<HTMLDivElement, DataTableProps>((props, ref) => {
  const { visibleColumns, searchTerm, applyTrigger, isResetClicked, installNumber, onFilteredDataChange, rPermission } =
    props;

  const dispatch = useAppDispatch();
  const dashboardName = useAppSelector((state) => state.AssetAnalysis.chartType);
  const isRLDashboard = dashboardName?.toLowerCase().includes('rl');
  const [appliedVisibleColumns, setAppliedVisibleColumns] = useState<string[]>([]);
  const [selectedRowData, setSelectedRowData] = useState<TableDataItem | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [activePopupRowId, setActivePopupRowId] = useState<string | number | null>(null);
  const [currentFilters, setCurrentFilters] = useState<any[]>([]);
  const [internalSearchTerm, setInternalSearchTerm] = useState<string>('');
  const pumpCheckerPageData = useAppSelector((state: any) => state.testWellColumnsSlice.data?.widgets);
  const testWellDataRaw = useAppSelector((state: any) => state.testWellDataSlice.data);
  const t1WellTestData = useAppSelector((state: any) => state.DefaultWellTest.data);
  const isTestWellLoading = useAppSelector((state: any) => state.testWellDataSlice.loading);
  const wellTestId = useAppSelector((state: any) => state.WellTestIdSlice?.data?.WellId);
  const { enablePumpChecker } = useConfig();
  const effectivePumpChecker = enablePumpChecker && !isRLDashboard;

  const wellTestWidget = effectivePumpChecker
    ? pumpCheckerPageData?.find((obj: any) => obj.key === 'wellTestTable')
    : pumpCheckerPageData?.find((obj: any) => obj.key === 'defaultWellTestTable');
  const columnObjects = wellTestWidget?.widgetProperty?.widgetProperties || [];

  // Use appropriate data source based on effective pump checker
  const testWellData = effectivePumpChecker
    ? Array.isArray(testWellDataRaw)
      ? testWellDataRaw
      : []
    : t1WellTestData?.values || [];

  const previousFilteredDataRef = React.useRef<TableDataItem[]>([]);

  const data = useMemo(() => {
    const mappedData = testWellData?.map((item: TableDataItem) => {
      // Handle date field differences between default and T2 data
      const dateField = effectivePumpChecker ? item.Date : item.TestDate;

      return {
        ...item,
        Date: dateField,
        Duration: item.Duration || 0,
        Info: item.Info || '-',
        Enabled: item.Enabled ? 'Y' : 'N',
        Oil: item.Oil || 0,
        Water: item.Water || 0,
        Gas: item.Gas || 0,
        TotalRate: item.LiquidFlowrate || 0,
        WaterCut: item.WaterCut || 0,
        TotalGasLiquidRatio: item.TotalGasLiquidRatio || 0,
        TotalGasOilRatio: item.TotalGasOilRatio || 0,
        ProductivityIndex: item.ProductivityIndex || 0,
        FormationGasOilRatio: item.FormationGasOilRatio || 0,
        CasingPressure: item.CasingPressure || 0,
        FlowlinePressure: item.FlowlinePressure || 0,
        ChokeDeltaPressure: item.ChokeDeltaPressure || 0,
        ProductionChokePosition: item.ProductionChokePosition || 0,
        ReservoirPressure: item.ReservoirPressure || 0,
        ResultsPwf: item.ResultsPwf || 0,
        WaterDensity: item.WaterDensity || 0,
        InstallNumber: item.InstallNumber || 0,
        StandardDeviation: item.StandardDeviation || 0,
        HasStandardDeviation: item.HasStandardDeviation || false,
        IsQuestionable: item.IsQuestionable || false,
        FormattedValidationMessages: item.FormattedValidationMessages || '-',
        PumpDischargePressure: item.PumpDischargePressure || 0,
        PumpDischargeTemperature: item.PumpDischargeTemperature || 0,
        ResultsInRange: item.ResultsInRange || '-',
        MotorCurrent: item.MotorCurrent || 0,
        CompletionMotorNameplateAmps: item.CompletionMotorNameplateAmps || 0,
        MotorLoad: item.MotorLoad || 0,
        CompletionVsdNameplateKva: item.CompletionVsdNameplateKva || 0,
        MotorVoltage: item.MotorVoltage || 0,
        MotorTemperature: item.MotorTemperature || 0,
        PowerKVA: item.PowerKVA || 0,
        DriveLoad: item.DriveLoad || 0,
        ResultsCalculatedIntakePressure: item.ResultsCalculatedIntakePressure || 0,
        ShowDD: item.ShowDD || 0,
        ResultsCalculatedDischargePressure: item.ResultsCalculatedDischargePressure || 0,
        SeparationEfficiency: item.SeparationEfficiency || 0,
        LiquidSeparationEfficiency: item.LiquidSeparationEfficiency || 0,
        ResultsPumpDegradation: item.ResultsPumpDegradation || 0,
        ResultsLostGrossRate: item.ResultsLostGrossRate || 0,
        ResultsLostOil: item.ResultsLostOil || 0,
        DriveOutputVoltage: item.DriveOutputVoltage || 0,
        DriveOutputCurrent: item.DriveOutputCurrent || 0,
        PowerConsumption: item.PowerConsumption || 0,
        PowerConsumptionPerBarrel: item.PowerConsumptionPerBarrel || 0,
        LiftCostPerDay: item.LiftCostPerDay || 0,
        LiftCostPerFluidBarrel: item.LiftCostPerFluidBarrel || 0,
        LiftCostPerOilBarrel: item.LiftCostPerOilBarrel || 0,
        TestViewModelsId: item.TestViewModelsId || '-',
        EspDiagnosisViewModel: item.EspDiagnosisViewModel || '-',
        PumpTrueVertical: item.PumpTrueVertical || 0,
        PerMagneticMotor: item.PerMagneticMotor || 0,
        MotorToPumpSpeedRatio: item.MotorToPumpSpeedRatio || 0,
        SubCool: item.SubCool || false,
        DownholePressure: item.DownholePressure || 0,
        DownholeTemperature: item.DownholeTemperature || 0,
        InjectionPressure: item.InjectionPressure || 0,
        InjectionRate: item.InjectionRate || 0,
        FormationGasLiquidRatio: item.FormationGasLiquidRatio || 0,
        FlowPath: item.FlowPath || 0,
        FlowPathString: item.FlowPathString || '-',
        LiquidFlowrate: item.LiquidFlowrate || 0,
        AnnulusChokeSettings: item.AnnulusChokeSettings || '-',
        OilGasInjectionRate: item.OilGasInjectionRate || 0,
        InjectionDepthMaximum: item.InjectionDepthMaximum || 0,
        InjectionMandrel: item.InjectionMandrel || '-',
        OrificeDepth: item.OrificeDepth || 0,
        VariationId: item.VariationId || '-',
        CalcWellheadTemperature: item.CalcWellheadTemperature || 0,
        IsHighlighted: false, // Initialize all rows as not highlighted
        IsHighlightedCell: 'totalRate',
        WellheadPressure: item.WellheadPressure || 0,
        GasInjectionRate: item.GasInjectionRate || 0,
        InjectionChoke: item.InjectionChoke || 0,
      };
    });
    return mappedData;
  }, [testWellData]);

  const baseData = useMemo(() => {
    if (!data) return [] as TableDataItem[];
    return installNumber !== null
      ? data.filter((d: { InstallNumber: number }) => d.InstallNumber === installNumber)
      : data;
  }, [data, installNumber]);

  const [tableRows, setTableRows] = useState(baseData ?? []);
  const [filteredData, setFilteredData] = useState(tableRows);

  const dataRef = useRef(data);
  const currentFiltersRef = useRef(currentFilters);
  const tableRowsRef = useRef(tableRows);

  useEffect(() => {
    currentFiltersRef.current = currentFilters; // Keep the ref updated with the latest value of currentFilters
  }, [currentFilters]);

  useEffect(() => {
    dataRef.current = data; // Keep the ref updated with the latest value of data
  }, [data]);

  useEffect(() => {
    tableRowsRef.current = tableRows; // Keep the ref updated with the latest value of currentFilters
  }, [tableRows]);
  const fixedColumns = Array.isArray(columnObjects?.fixedColumns)
    ? columnObjects.fixedColumns.map((col: any) => col.label)
    : [];

  const arraysAreEqual = (arr1: string[], arr2: string[]) => {
    if (arr1.length !== arr2.length) return false;
    return arr1.every((value, index) => value === arr2[index]);
  };

  // (Removed earlier separate effect) We'll emit changes based on final displayed rows only.

  useEffect(() => {
    if (wellTestId !== undefined && wellTestId !== null) {
      dispatch(
        fetchWellTestData({
          wellId: wellTestId,
          installNumber: installNumber,
        }),
      );
    }
  }, [wellTestId, installNumber]);

  //const [filteredTableRows, setFilteredTableRows] = useState(data ?? []);
  useEffect(() => {
    if (testWellData) {
      if (JSON.stringify(baseData) !== JSON.stringify(tableRows)) {
        setTableRows(baseData);
      }
    }
  }, [baseData, visibleColumns]);

  useEffect(() => {
    if (applyTrigger && !arraysAreEqual(visibleColumns, appliedVisibleColumns)) {
      setAppliedVisibleColumns(visibleColumns);
    }
  }, [applyTrigger, visibleColumns]);

  useEffect(() => {
    if (isResetClicked) {
      setAppliedVisibleColumns(visibleColumns);
    }
  }, [isResetClicked]);

  useEffect(() => {
    if (columnObjects?.columns?.length && !applyTrigger) {
      setAppliedVisibleColumns([...visibleColumns]);
    }
  }, [columnObjects?.columns]);

  // data types
  const customIsNumericField = React.useCallback(
    (columnId: string) => {
      if (originalIsNumericField && originalIsNumericField(columnId)) {
        return true;
      }

      if (data && data.length > 0) {
        const sampleValue = data[0][columnId as keyof TableDataItem];
        return typeof sampleValue === 'number';
      }

      return false;
    },
    [data],
  );

  const multiConditionFilter = React.useMemo(() => {
    return createMultiConditionFilter(setCurrentFilters, currentFiltersRef, true, undefined, customIsNumericField);
  }, [customIsNumericField]);

  const handleEditWellTestClick = (rowData: TableDataItem, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedRowData(rowData);
    setIsEditModalOpen(true);
  };
  // Function to handle edit Icon click
  const handleEditClick = (rowData: TableDataItem, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent row selection when clicking the edit Icon

    const formattedData = Object.entries(rowData)
      .filter(([key]) => key !== 'IsHighlighted' && key !== 'IsHighlightedCell')
      .map(([key, value]) => {
        // Format arrays or objects for better display
        let displayValue = value;
        if (Array.isArray(value)) {
          displayValue = 'Array of icons';
        } else if (typeof value === 'object' && value !== null) {
          displayValue = JSON.stringify(value);
        }
        return `${key}: ${displayValue}`;
      })
      .join('\n');

    alert(`Row Data:\n${formattedData}`);
  };
  // Function to handle row click for selection
  const handleRowClick = (rowIndex: number) => {
    setFilteredData((prev: any) =>
      prev.map((row: any, index: any) => ({
        ...row,
        IsHighlighted: index === rowIndex ? true : false,
      })),
    );
  };

  if (fixedColumns && !fixedColumns.includes('Icon')) {
    fixedColumns.unshift('Icon');
  }
  const mergedColumns: ColumnData[] = [
    ...(Array.isArray(columnObjects?.columns) ? columnObjects.columns : []),
    ...(Array.isArray(columnObjects?.fixedColumns) ? columnObjects.fixedColumns : []),
  ].sort((a, b) => a.order - b.order);

  const columnDefs: { accessor: keyof TableDataItem; label: string }[] = mergedColumns
    .filter((col: ColumnData) => col.key)
    .map((col: ColumnData) => ({
      accessor: col.key as keyof TableDataItem,
      label: col.label,
    }));

  const fixedColumnsDefs = fixedColumns.map((key: string) => ({
    accessor: key,
    label: key,
  }));

  const updatedColumnDefs = [
    ...fixedColumnsDefs,
    ...columnDefs.filter((column) => !fixedColumnsDefs.some((fixedCol: any) => fixedCol.accessor === column.accessor)),
  ];

  const columns: Column<TableDataItem>[] = useMemo(() => {
    const filteredColumnDefs = updatedColumnDefs.filter(({ accessor }: any) => {
      // Filter out Info column when effective pump checker is false
      return !(accessor === 'Info' && !effectivePumpChecker);
    });

    return filteredColumnDefs.map(({ accessor, label }: any) => {
      if (accessor === 'Icon') {
        return {
          accessor,
          Header: '',
          Cell: ({ value, row }: { value: string; row: any }) => (
            <ProtectAction canAccess={rPermission.canEditTest}>
              <div className='ico-edit flex justify-center' onClick={(e) => handleEditWellTestClick(row.original, e)}>
                <div
                  style={{
                    width: '20px',
                    height: '20px',
                    backgroundImage: `url(${value})`,
                    backgroundSize: 'cover',
                    backgroundRepeat: 'no-repeat',
                  }}
                />
              </div>
            </ProtectAction>
          ),
        } as Column<TableDataItem>;
      }

      if (accessor === 'Date') {
        return {
          accessor,
          Header: () => (
            <div className='flex gap-[4px] items-center ' title={'Date'}>
              <span className='text-[12px] font-semibold text-[#f7f9f9]' title={'Date'}>
                Date
              </span>
            </div>
          ),
          filter: multiConditionFilter,
          Cell: ({ value, row }: { value: string; row: any }) => (
            <div className='flex items-center justify-center'>
              <div onClick={(e) => handleEditClick(row.original, e)} title='Edit row' className='edit-icon' />
              <span style={{ color: '#60bfda' }}>{formatDate(value)}</span>
            </div>
          ),
        } as Column<TableDataItem>;
      }

      if (accessor === 'Info') {
        return {
          accessor,
          Header: () => (
            <div className='flex gap-[4px] items-center' title={'Info'}>
              <span className='text-[12px] font-semibold text-[#f7f9f9]' title={'Info'}>
                Info
              </span>
            </div>
          ),
          Cell: ({ row }: { row: any }) => {
            const hasInfo = row.original.Processed === false || row.original.ValidationMessages !== null;
            const hasComments = row.original.Comment && row.original.Comment !== null;
            const validationMessage = row.original.ValidationMessages;
            const comment = row.original.Comment;
            const rowId = row.original.Id;
            return (
              <div className='flex justify-center items-center gap-2'>
                {hasInfo && (
                  <InfoPopup
                    trigger={
                      <div className='tight-info-tooltip'>
                        <Tooltip content='Test Information' direction='left'>
                          <InfoIcon />
                        </Tooltip>
                      </div>
                    }
                    messages={{ validationMessage }}
                    onToggle={(open) => {
                      setActivePopupRowId(open ? rowId : null);
                    }}
                  ></InfoPopup>
                )}
                {hasComments && (
                  <CommentPopup
                    trigger={
                      <div className='tight-comment-tooltip'>
                        <Tooltip content='Comment' direction='left'>
                          <CommentIcon />
                        </Tooltip>
                      </div>
                    }
                    onToggle={(open) => {
                      setActivePopupRowId(open ? rowId : null);
                    }}
                  >
                    {comment}
                  </CommentPopup>
                )}
                {!hasInfo && !hasComments && <span className='text-center'>-</span>}
              </div>
            );
          },
        } as Column<TableDataItem>;
      }

      return {
        accessor,
        Header: () => (
          <div className='flex gap-[4px] items-center' title={accessor}>
            <span className='text-[12px] font-semibold text-[#f7f9f9]' title={accessor}>
              {label}
            </span>
          </div>
        ),
        filter: multiConditionFilter,
        Cell: ({ value }: { value: any }) => {
          if (value === null || value === undefined || value === '-') {
            return <span className='text-center'>-</span>;
          }

          return <span className='text-right'>{typeof value === 'number' ? value.toFixed(2) : value}</span>;
        },
      } as Column<TableDataItem>;
    });
  }, [data, visibleColumns]);

  const visibleColumnsData = useMemo(() => {
    const fixed = columns.filter((column) => fixedColumns.includes(column.accessor as string));

    const scrollable = columns
      .filter(
        (column) =>
          !fixedColumns.includes(column.accessor as string) &&
          appliedVisibleColumns.includes(column.accessor as string),
      )
      .sort(
        (a, b) =>
          appliedVisibleColumns.indexOf(a.accessor as string) - appliedVisibleColumns.indexOf(b.accessor as string),
      );
    return [...fixed, ...scrollable];
  }, [appliedVisibleColumns]);

  // Custom search filter that handles numeric formatting and special cases
  const customSearchFilter = React.useCallback(
    (data: TableDataItem[], searchTerm: string) => {
      if (!searchTerm.trim()) return data;
      const searchValue = searchTerm.toLowerCase();
      const visibleColumnAccessors = visibleColumnsData.map((col) => col.accessor as string);

      return data.filter((row) =>
        Object.entries(row).some(([key, value]) => {
          if (key === 'IsHighlighted' || key === 'IsHighlightedCell' || value == null) return false;
          if (!visibleColumnAccessors.includes(key)) return false;
          if (key === 'Enabled') return String(value).toLowerCase() === searchValue;
          if (typeof value === 'boolean') return false;
          if (typeof value === 'number') {
            return (
              String(value).toLowerCase().includes(searchValue) || value.toFixed(2).toLowerCase().includes(searchValue)
            );
          }
          return String(value).toLowerCase().includes(searchValue);
        }),
      );
    },
    [visibleColumnsData],
  );

  const defaultColumn: any = React.useMemo(
    () => ({
      Filter: (props: any) => (
        <DefaultColumnFilter
          {...props}
          clearFilterForColumn={clearFilterForColumn}
          currentFiltersRef={currentFiltersRef}
        />
      ),
    }),
    [],
  );
  // Apply search filter when searchTerm changes
  useEffect(() => {
    if (JSON.stringify(filteredData) !== JSON.stringify(tableRows)) {
      setFilteredData(tableRows);
    }
  }, [tableRows, visibleColumns, filteredData]);

  const instance = useTable<TableDataItem>(
    {
      columns: visibleColumnsData,
      data: filteredData,
      defaultColumn: defaultColumn,
    },
    useFilters,
    useGlobalFilter,
    useSortBy,
  ) as ExtendedTableInstance<TableDataItem> & { state: ExtendedTableState<TableDataItem> };

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow, setAllFilters } =
    instance as ExtendedTableInstance<TableDataItem> & {
      state: ExtendedTableState<TableDataItem>;
      setAllFilters: (filters: any[]) => void;
    };

  // Apply custom search filtering
  const finalRows = useMemo(() => {
    const effectiveSearchTerm = searchTerm || internalSearchTerm || '';
    if (!effectiveSearchTerm.trim()) return rows;

    const filteredData = customSearchFilter(
      rows.map((row) => row.original),
      effectiveSearchTerm,
    );
    return rows.filter((row) => filteredData.includes(row.original));
  }, [rows, customSearchFilter, searchTerm, internalSearchTerm]);

  useEffect(() => {
    if (searchTerm !== undefined) {
      setInternalSearchTerm(searchTerm);
    }
  }, [searchTerm]);

  const clearFilterForColumn = (columnId: string) => {
    const remainingFilters = currentFiltersRef.current.filter((filter) => filter.columnId !== columnId);
    currentFiltersRef.current = remainingFilters;
    setCurrentFilters([...remainingFilters]);

    setAllFilters(
      remainingFilters.length === 0
        ? []
        : remainingFilters.map((filter) => ({ id: filter.columnId, value: filter.filterValue })),
    );
  };

  // Fire onFilteredDataChange whenever finalRows change
  useEffect(() => {
    if (!onFilteredDataChange) return;
    const displayed = finalRows.map((r: any) => r.original);
    const displayedJson = JSON.stringify(displayed);

    if (displayedJson !== JSON.stringify(previousFilteredDataRef.current)) {
      previousFilteredDataRef.current = displayed;
      onFilteredDataChange(displayed);
    }
  }, [finalRows, onFilteredDataChange]);

  useEffect(() => {
    const htmlString = ReactDOMServer.renderToStaticMarkup(
      <div className='flex pt-[7px] pr-[12px] pb-[6px] pl-[12px] gap-[8px] items-center self-stretch shrink-0 flex-nowrap bg-[#001023] rounded-[8px] border-solid border border-[#4a5463] relative shadow-[0_1px_2px_0_rgba(16,24,40,0.05)] search-input w-full'>
        <span className='ico-search' />
        <div className='flex w-full gap-[8px] items-center shrink-0 flex-nowrap relative'>
          <input
            type='text'
            value={internalSearchTerm || ''}
            onChange={(e) => {
              setInternalSearchTerm(e.target.value || '');
            }}
            placeholder='Search...'
            className="flex w-full h-[24px] justify-start items-start shrink-0 basis-auto font-['Mulish'] text-[16px] font-normal leading-[24px] text-[#b8c5cc] relative text-left whitespace-nowrap bg-transparent border-none"
          />
        </div>
      </div>,
    );
    if (ref && 'current' in ref && ref.current) {
      ref.current.innerHTML = htmlString; // Updating the HTML content
      ref.current.onkeyup = (e: Event) => {
        const inputEvent = e as unknown as React.ChangeEvent<HTMLInputElement>;
        setInternalSearchTerm(inputEvent.target.value || '');
      };
    }
  }, [internalSearchTerm]);

  if (!Array.isArray(testWellData)) {
    return null;
  }

  return (
    <section className='table-container-wrapper'>
      <div className={`table-container ${finalRows.length === 0 && !isTestWellLoading ? 'empty-table' : ''}`}>
        {isTestWellLoading && (
          <div className='absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2'>
            <Loader />
          </div>
        )}
        {/* <GlobalFilter globalFilter={globalFilter} setGlobalFilter={setGlobalFilter} /> */}
        <div
          style={{ maxHeight: '450px', overflow: 'hidden' }}
          onMouseEnter={(e) => {
            e.currentTarget.style.overflow = 'auto';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.overflow = 'hidden';
          }}
        >
          <table {...getTableProps()} className='data-table'>
            <thead
              style={{
                position: 'sticky',
                top: 0,
                zIndex: effectivePumpChecker ? 10 : 1,
                backgroundColor: 'var(--background-color, #131f2e)',
              }}
            >
              {columnObjects.length === 0 ? (
                <tr className='h-20'>
                  <th colSpan={visibleColumns.length} className='text-center p-4 align-middle'>
                    <Loader />
                  </th>
                </tr>
              ) : (
                <>
                  {headerGroups.map((headerGroup) => (
                    <tr {...headerGroup.getHeaderGroupProps()}>
                      {headerGroup.headers.map((column: any) => {
                        const isFixedColumn = fixedColumns.includes(column.id);
                        const isIconColumn = column.id === 'Icon';
                        const isDateColumn = column.id === 'Date';
                        const isInfoColumn = column.id === 'Info';

                        return (
                          <th
                            {...column.getHeaderProps({
                              ...column.getSortByToggleProps(),
                              title: '',
                            })}
                            className={`h-[44px] pt-[16px] pr-[12px] pb-[16px] pl-[12px] border-solid border-t border-t-[#4a5463] text-left ${
                              isFixedColumn ? 'fixed-column' : ''
                            } ${isIconColumn ? 'Icon-column' : ''} ${finalRows.length === 0 ? 'Icon-column-width' : ''} ${isDateColumn ? 'Date-column' : ''} ${
                              isInfoColumn ? 'Info-column' : ''
                            } ${column.id}-column`}
                          >
                            <div>{column.render('Header')}</div>
                            <div>{column.isSorted ? column.isSortedDesc ? <ArrowDown /> : <ArrowUp /> : ''}</div>
                            {isInfoColumn && <div className='Info-column-divider'></div>}

                            <div>{column.canFilter ? column.render('Filter') : null}</div>
                          </th>
                        );
                      })}
                    </tr>
                  ))}
                </>
              )}
            </thead>
            <tbody {...getTableBodyProps()}>
              {finalRows.length > 0 &&
                finalRows.map((row, rowIndex) => {
                  prepareRow(row);
                  return (
                    <tr
                      {...row.getRowProps()}
                      className={`cursor-pointer ${row.original.IsHighlighted ? 'highlighted-row' : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRowClick(rowIndex);
                      }}
                    >
                      {row.cells.map((cell) => {
                        const isFixedColumn = fixedColumns.includes(cell.column.id);
                        const isIconColumn = cell.column.id === 'Icon';
                        const isDateColumn = cell.column.id === 'Date';
                        const isInfoColumn = cell.column.id === 'Info';

                        return (
                          <td
                            {...cell.getCellProps()}
                            className={`border-t border-t-[#4a5463] p-[12px] ${
                              isFixedColumn ? 'fixed-column' : ''
                            } ${isIconColumn ? 'Icon-column' : ''}  ${isDateColumn ? 'Date-column' : ''} ${
                              isInfoColumn ? 'Info-column' : ''
                            }
                             ${isInfoColumn && activePopupRowId === row.original.Id ? 'info-active' : ''}
                            `}
                          >
                            {cell.render('Cell')}
                            {isInfoColumn && <div className='Info-column-divider'></div>}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
        {isEditModalOpen &&
          effectivePumpChecker &&
          (dashboardName?.includes('esp') ? (
            <AddWellTestModal
              modalTitle='Edit Test'
              buttonText='Save'
              key='edit-esp'
              onClose={() => setIsEditModalOpen(false)}
              initialData={selectedRowData ? mapTableDataToFormData(selectedRowData) : undefined}
              disabled
              installNumber={installNumber}
            />
          ) : (
            <AddGLWellTestModal
              modalTitle='Edit Test'
              buttonText='Save'
              key='edit-gl'
              onClose={() => setIsEditModalOpen(false)}
              initialData={selectedRowData ? mapGLTableDataToFormData(selectedRowData) : undefined}
              installNumber={installNumber}
            />
          ))}
        {isEditModalOpen && !effectivePumpChecker && (
          <AddDefaultWellTestModal
            modalTitle='Edit Well Test'
            buttonText='Save'
            onClose={() => setIsEditModalOpen(false)}
            initialData={selectedRowData ? mapDefaultWellTestTableDataToForm(selectedRowData) : undefined}
            disabled={true}
          />
        )}
      </div>
      {finalRows.length === 0 && (
        <div className='no-matching-records-container text-center p-4 pl-6 pr-6'>
          <span className='no-matching-records-message'>No matching records found</span>
        </div>
      )}

      <TableLegend />
    </section>
  );
});

export default DataTable;
